import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Zap, Shield, Clock, CheckCircle, XCircle, LogOut } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";

const Dashboard_v2 = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API}/member/dashboard`, {
        withCredentials: true
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  if (!data?.tier || data.tier === 'none') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Shield className="h-16 w-16 text-slate-500 mx-auto mb-4" />
          <h1 className="text-3xl font-heading font-bold mb-4 text-white">No Active Membership</h1>
          <p className="text-slate-400 mb-6">
            Subscribe to a tier to activate your safety net protection.
          </p>
          <Button 
            data-testid="subscribe-now-btn"
            onClick={() => navigate('/select-tier')}
            className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full px-8 py-6 text-lg font-bold"
          >
            Subscribe Now
          </Button>
        </div>
      </div>
    );
  }

  const tierColors = {
    essential: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    standard: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    plus: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
  };

  const statusIcons = {
    pending: <Clock className="h-5 w-5 text-yellow-400" />,
    approved: <CheckCircle className="h-5 w-5 text-green-400" />,
    declined: <XCircle className="h-5 w-5 text-red-400" />
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-heading font-bold text-white">DueDay</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400">Hi, {data?.user?.name}</span>
            <Button 
              data-testid="nav-billing-btn"
              variant="ghost" 
              onClick={() => navigate('/billing')}
              className="rounded-full text-white hover:text-yellow-400 hover:bg-white/10"
            >
              Billing
            </Button>
            {data?.user?.role === 'admin' && (
              <Button 
                data-testid="nav-admin-btn"
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="rounded-full text-white hover:text-yellow-400 hover:bg-white/10"
              >
                Admin
              </Button>
            )}
            <Button 
              data-testid="logout-btn"
              variant="ghost" 
              onClick={handleLogout}
              className="rounded-full text-white hover:text-yellow-400 hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Membership Card */}
          <div 
            className="col-span-1 md:col-span-2 bg-slate-800/50 border border-slate-700 rounded-2xl p-8" 
            data-testid="membership-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-slate-400 mb-1">Your Membership</p>
                <h2 className="text-3xl font-heading font-bold capitalize text-white">{data.tier} Plan</h2>
              </div>
              <div className={`px-4 py-2 rounded-full border ${tierColors[data.tier] || 'bg-slate-700'}`}>
                <Shield className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Per Event Limit:</span>
                <span className="font-semibold text-white">${data.limits?.per_event || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">30-Day Limit:</span>
                <span className="font-semibold text-white">${data.limits?.per_30_days || 0}</span>
              </div>
            </div>
          </div>

          {/* Quick Action Card */}
          <div 
            className="col-span-1 md:col-span-2 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl p-8" 
            data-testid="quick-action-card"
          >
            <h3 className="text-2xl font-heading font-bold mb-4 text-black">Need Help with a Bill?</h3>
            <p className="text-black/70 mb-6">
              Submit a request and we'll review it within 24 hours. We're here to help you keep essential bills paid on time.
            </p>
            <Button 
              data-testid="request-help-btn"
              onClick={() => navigate('/request-help')}
              className="bg-black text-yellow-400 hover:bg-black/90 rounded-full px-8 py-6 text-lg font-bold shadow-lg"
            >
              Request Help Now
            </Button>
          </div>

          {/* Recent Requests */}
          <div 
            className="col-span-1 md:col-span-4 bg-slate-800/50 border border-slate-700 rounded-2xl p-8" 
            data-testid="recent-requests-card"
          >
            <h3 className="text-2xl font-heading font-bold mb-6 text-white">Recent Requests</h3>
            {data.recent_requests && data.recent_requests.length > 0 ? (
              <div className="space-y-4">
                {data.recent_requests.map((request) => (
                  <div 
                    key={request.request_id}
                    data-testid={`request-${request.request_id}`}
                    className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl border border-slate-700"
                  >
                    <div className="flex items-center gap-4">
                      {statusIcons[request.status]}
                      <div>
                        <p className="font-semibold capitalize text-white">{request.bill_type.replace('-', ' ')}</p>
                        <p className="text-sm text-slate-400">
                          {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-white">${request.amount_requested}</p>
                      <p className="text-sm text-slate-400 capitalize">{request.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No requests yet. Submit your first help request above.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard_v2;
